import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff, AlertCircle, CheckCircle } from 'lucide-react';
import axios from 'axios';
import ClearPathLogo from '../components/ClearPathLogo';
import { useAuth } from '../contexts/AuthContext';
import { POLICY_VERSION } from './TermsPage';

type Mode = 'login' | 'register' | 'forgot';

export default function AuthPage() {
  const [mode, setMode] = useState<Mode>('login');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login, register, googleLogin } = useAuth();
  const [searchParams] = useSearchParams();

  const oauthError = searchParams.get('error');

  const switchMode = (newMode: Mode) => {
    setMode(newMode);
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      if (mode === 'register') {
        if (!agreed) {
          setError('Please agree to the Terms and Privacy Policy to continue.');
          setLoading(false);
          return;
        }
        if (password.length < 8) {
          setError('Password must be at least 8 characters');
          setLoading(false);
          return;
        }
        await register(name, email, password, POLICY_VERSION);
        navigate('/onboarding');
      } else if (mode === 'login') {
        await login(email, password);
        navigate('/home');
      }
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      if (detail) {
        setError(detail);
      } else if (!err.response) {
        setError('Unable to reach the server. Please check your connection.');
      } else {
        setError(mode === 'login'
          ? 'Login failed. Please check your email and password.'
          : 'Registration failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      await axios.post('/api/auth/forgot-password', { email });
      setSuccess('If that email exists in our system, a reset link has been sent. Check your inbox.');
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const headingText = mode === 'login' ? 'Welcome back' : mode === 'register' ? 'Create your account' : 'Reset your password';
  const subText = mode === 'login' ? 'Sign in to your ClearPath account' : mode === 'register' ? 'Start making smarter business decisions' : 'Enter your email and we\'ll send you a reset link';

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
      className="min-h-screen bg-cream flex"
    >
      {/* Left: branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#4A2D5E] items-center justify-center p-12">
        <div className="max-w-md">
          <ClearPathLogo variant="white" size="lg" />
          <h2 className="mt-10 font-serif text-3xl text-white leading-tight">
            Your data finally talks back.
          </h2>
          <p className="mt-4 font-sans text-white/70 leading-relaxed">
            ClearPath turns your POS data into plain-English insights — so you can make smarter decisions, every single day.
          </p>
        </div>
      </div>

      {/* Right: form */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8">
            <ClearPathLogo size="md" />
          </div>

          <h1 className="font-serif text-2xl text-text-primary">{headingText}</h1>
          <p className="mt-1 font-sans text-sm text-text-secondary">{subText}</p>

          {(error || oauthError) && (
            <div className="mt-4 flex items-center gap-2 p-3 bg-[#B54A4A]/10 border border-[#B54A4A]/20 rounded-[8px]">
              <AlertCircle size={16} strokeWidth={1.5} className="text-[#B54A4A] flex-shrink-0" />
              <p className="text-sm font-sans text-[#B54A4A]">
                {error || 'OAuth login failed. Please try again.'}
              </p>
            </div>
          )}

          {success && (
            <div className="mt-4 flex items-start gap-2 p-3 bg-[#2E6B4F]/10 border border-[#2E6B4F]/20 rounded-[8px]">
              <CheckCircle size={16} strokeWidth={1.5} className="text-[#2E6B4F] flex-shrink-0 mt-0.5" />
              <p className="text-sm font-sans text-[#2E6B4F]">{success}</p>
            </div>
          )}

          {/* Forgot password form */}
          {mode === 'forgot' ? (
            <form onSubmit={handleForgotPassword} className="mt-6 space-y-4">
              <div>
                <label className="block text-sm font-sans font-medium text-text-primary mb-1.5">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="you@example.com"
                  className="w-full bg-white border border-[#E6E2DE] focus:ring-2 focus:ring-[#4A2D5E]/20 focus:border-[#4A2D5E] rounded-[8px] h-11 font-sans text-sm px-3 outline-none transition-all"
                />
              </div>
              <button
                type="submit"
                disabled={loading || !!success}
                className="w-full bg-[#4A2D5E] text-white hover:bg-[#3D254D] rounded-[8px] shadow-sm font-sans font-medium px-6 py-2.5 transition-colors disabled:opacity-50"
              >
                {loading ? 'Sending...' : 'Send Reset Link'}
              </button>
              <p className="text-center text-sm font-sans text-text-secondary">
                <button type="button" onClick={() => switchMode('login')} className="text-[#4A2D5E] font-medium hover:underline">
                  Back to sign in
                </button>
              </p>
            </form>
          ) : (
            <>
              {/* Google OAuth.
                  The consent checkbox below only governs the email/password
                  form, and this button is shared by both sign-in and sign-up,
                  so a Google sign-up used to create an account with no consent
                  record at all. The backend now stores one for these users;
                  this notice is what makes that record truthful. Do not remove
                  one without the other. */}
              <p className="mt-6 text-xs text-text-secondary font-sans text-center">
                By continuing with Google you agree to our{' '}
                <Link to="/terms" className="underline hover:text-text-primary">Terms of Service</Link>
                {' '}and{' '}
                <Link to="/privacy" className="underline hover:text-text-primary">Privacy Policy</Link>.
              </p>
              <button
                onClick={googleLogin}
                className="mt-3 w-full flex items-center justify-center gap-3 bg-white border border-[#E6E2DE] hover:bg-[#FAF9F7] rounded-[8px] h-11 font-sans text-sm text-text-primary transition-colors"
              >
                <svg width="18" height="18" viewBox="0 0 18 18">
                  <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615Z" fill="#4285F4"/>
                  <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.26c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18Z" fill="#34A853"/>
                  <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.997 8.997 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332Z" fill="#FBBC05"/>
                  <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58Z" fill="#EA4335"/>
                </svg>
                Continue with Google
              </button>

              <div className="my-6 flex items-center gap-3">
                <div className="flex-1 h-px bg-[#E6E2DE]" />
                <span className="text-xs font-sans text-text-secondary">or</span>
                <div className="flex-1 h-px bg-[#E6E2DE]" />
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {mode === 'register' && (
                  <div>
                    <label className="block text-sm font-sans font-medium text-text-primary mb-1.5">Name</label>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                      placeholder="Your name"
                      className="w-full bg-white border border-[#E6E2DE] focus:ring-2 focus:ring-[#4A2D5E]/20 focus:border-[#4A2D5E] rounded-[8px] h-11 font-sans text-sm px-3 outline-none transition-all"
                    />
                  </div>
                )}
                <div>
                  <label className="block text-sm font-sans font-medium text-text-primary mb-1.5">Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="you@example.com"
                    className="w-full bg-white border border-[#E6E2DE] focus:ring-2 focus:ring-[#4A2D5E]/20 focus:border-[#4A2D5E] rounded-[8px] h-11 font-sans text-sm px-3 outline-none transition-all"
                  />
                </div>
                <div>
                  <label className="block text-sm font-sans font-medium text-text-primary mb-1.5">Password</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      placeholder={mode === 'register' ? 'Min 8 characters' : 'Your password'}
                      className="w-full bg-white border border-[#E6E2DE] focus:ring-2 focus:ring-[#4A2D5E]/20 focus:border-[#4A2D5E] rounded-[8px] h-11 font-sans text-sm px-3 pr-10 outline-none transition-all"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary hover:text-text-primary"
                    >
                      {showPassword ? <EyeOff size={16} strokeWidth={1.5} /> : <Eye size={16} strokeWidth={1.5} />}
                    </button>
                  </div>
                </div>
                {mode === 'register' && (
                  <label className="flex items-start gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={agreed}
                      onChange={(e) => setAgreed(e.target.checked)}
                      className="mt-0.5 h-4 w-4 rounded border-[#E6E2DE] text-[#4A2D5E] focus:ring-[#4A2D5E]/20"
                    />
                    <span className="text-xs font-sans text-text-secondary leading-relaxed">
                      I agree to the{' '}
                      <Link to="/terms" target="_blank" className="text-[#4A2D5E] hover:underline">Terms of Service</Link>{' '}and{' '}
                      <Link to="/privacy" target="_blank" className="text-[#4A2D5E] hover:underline">Privacy Policy</Link>.
                    </span>
                  </label>
                )}
                {mode === 'login' && (
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => switchMode('forgot')}
                      className="text-xs font-sans text-[#4A2D5E] hover:underline"
                    >
                      Forgot password?
                    </button>
                  </div>
                )}
                <button
                  type="submit"
                  disabled={loading || (mode === 'register' && !agreed)}
                  className="w-full bg-[#4A2D5E] text-white hover:bg-[#3D254D] rounded-[8px] shadow-sm font-sans font-medium px-6 py-2.5 transition-colors disabled:opacity-50"
                >
                  {loading ? 'Please wait...' : mode === 'login' ? 'Sign In' : 'Create Account'}
                </button>

                {mode === 'register' && (
                  <p className="mt-3 text-center text-xs font-sans text-[#6B6670] leading-relaxed">
                    By creating an account you agree to our{' '}
                    <Link to="/terms" className="text-[#4A2D5E] hover:underline">Terms of Service</Link>
                    {' '}and{' '}
                    <Link to="/privacy" className="text-[#4A2D5E] hover:underline">Privacy Policy</Link>.
                  </p>
                )}
              </form>

              <p className="mt-6 text-center text-sm font-sans text-text-secondary">
                {mode === 'login' ? "Don't have an account? " : 'Already have an account? '}
                <button
                  onClick={() => switchMode(mode === 'login' ? 'register' : 'login')}
                  className="text-[#4A2D5E] font-medium hover:underline"
                >
                  {mode === 'login' ? 'Sign up' : 'Sign in'}
                </button>
              </p>
            </>
          )}
        </div>
      </div>
    </motion.div>
  );
}
